import React from 'react';
import clsx from 'clsx';
import { EnhancedTableHeader } from '.';
import EnhancedTableBodyCell from './EnhancedTableBodyCell';
import { ITableRowSettings } from '../..';
import Loader from '@components/loader/Loader';
import { makeStyles } from 'tss-react/mui';
import { TableBody, TableCell, TableRow, lighten } from '@mui/material';
import { MAIN_THEME } from '@styles';
import { IAbstractRecord } from '@models';
import EmptyTableRow from './EmptyTableRow';
import CheckboxFormField from '@components/form-fields/CheckboxFormField';

interface IEnhancedBodyProps {
  disableSelection?: boolean | DisableSelectionType;
  emptyRows: number;
  pageSize?: number;
  handleClick(
    name: string,
    checked: boolean,
    rowIndex?: string,
    rowId?: string
  ): void;
  handleCellValueChanged?(
    rowIndex: number,
    columnId: string,
    newValue: unknown
  ): void;
  handleBlur?(rowIndex: number, columnId: string, newValue: unknown): void;
  headers: Record<string, EnhancedTableHeader>;
  rowSettings?: ITableRowSettings;
  isSelected: (key: string) => boolean;
  rows?: IEnhancedRow[];
  entityName?: string;
  loader?: boolean;
  tableClasses?: {
    bodyRootClass?: string;
    cellRootClass?: string;
  };
  showCellFullData?: boolean;
}

export enum DisableSelectionType {
  'Hide' = 'Hide',
}

export interface IEnhancedRow {
  key: string;
  columns: IAbstractRecord;
  index: number;
  disabled?: boolean;
}

const useStyles = makeStyles()(() => ({
  bodyRoot: {
    '& tr:nth-of-type(odd)': {
      backgroundColor: MAIN_THEME.color.white98,
    },
  },
  rowRoot: {
    '&.Mui-selected': {
      backgroundColor: lighten(MAIN_THEME.color.black, 0.85),
    },
    '&.MuiTableRow-hover:hover': {
      backgroundColor: lighten(MAIN_THEME.color.black, 0.85),
    },
  },
  cellRoot: {
    color: `${MAIN_THEME.color.black} !important`,
    fontSize: `${MAIN_THEME.fontSize.sm}  !important`,
    lineHeight: '12px !important',
    height: '34px !important',
    border: 'none !important',
    padding: '4px 2px !important',
    fontFamily: MAIN_THEME.font.bold,
  },
  checkboxCellRoot: {
    width: '14px !important',
    padding: '0 14px 0 8px !important',
    border: 'none !important',
    height: '34px !important',
    position: 'relative !important' as 'relative',
    fontFamily: MAIN_THEME.font.regular,
  },
  checkboxCellRootWithMessage: {
    padding: '0 20px 0 8px !important',
  },
  noDataMessage: {
    textAlign: 'center',
    color: MAIN_THEME.color.white86,
  },
  noDataCell: {
    color: MAIN_THEME.color.black,
    border: 'none',
    height: '34px',
    fontSize: MAIN_THEME.fontSize.sm,
    lineHeight: '12px',
    fontFamily: MAIN_THEME.font.regular,
  },
  errorIconStyle: {
    height: '15px',
    width: '15px',
    color: 'red',
    position: 'absolute',
    top: '10px',
    right: '8px',
    cursor: 'pointer',
  },
  paddedCellRoot: {
    padding: '0px 6px 0px 20px !important',
  },
  selectablePaddedCellRoot: {
    padding: '0px 6px 0px 60px !important',
    fontSize: `${MAIN_THEME.fontSize.sm} !important`,
  },
}));

const EnhancedTableBody: React.FC<IEnhancedBodyProps> = ({
  emptyRows,
  pageSize,
  handleClick,
  handleCellValueChanged,
  handleBlur,
  headers,
  isSelected,
  rows,
  disableSelection,
  tableClasses,
  loader,
  showCellFullData,
}) => {
  const { classes } = useStyles();

  const getRootCellClassname = (headerIndex: number): string => {
    if (disableSelection && !headerIndex) {
      switch (typeof disableSelection) {
        case 'boolean':
          return disableSelection
            ? classes.paddedCellRoot
            : classes.selectablePaddedCellRoot;
        default:
          return classes.paddedCellRoot;
      }
    }
    return '';
  };

  return (
    <TableBody
      classes={{
        root: clsx(
          classes.bodyRoot,
          tableClasses ? tableClasses.bodyRootClass : undefined
        ),
      }}
    >
      {loader ? (
        <EmptyTableRow
          width={Object.keys(headers).length}
          height={38 * pageSize}
          classes={classes}
        >
          <Loader />
        </EmptyTableRow>
      ) : rows.length > 0 ? (
        rows.map((row, index) => {
          const isItemSelected = isSelected(row.key);
          const labelId = `enhanced-table-checkbox-${index}`;

          return (
            <TableRow
              hover
              role="checkbox"
              aria-checked={isItemSelected}
              tabIndex={-1}
              key={row.key}
              selected={isItemSelected}
              classes={{ root: classes.rowRoot }}
              onClick={() =>
                handleClick(
                  row.key,
                  !isItemSelected,
                  row.index.toString(),
                  row.columns.insurance_Subline_Id
                )
              }
            >
              {!disableSelection && (
                <TableCell
                  padding="none"
                  classes={{
                    root: clsx(classes.checkboxCellRoot),
                  }}
                >
                  <CheckboxFormField
                    checked={isItemSelected}
                    name={row.key}
                    onChange={() => handleClick}
                    inputOnly
                  />
                </TableCell>
              )}
              {Object.values(headers).map((header, headerIndex) => {
                return (
                  <EnhancedTableBodyCell
                    selected={isSelected(row.key)}
                    key={header.name + labelId}
                    row={row}
                    header={header}
                    className={clsx(
                      tableClasses
                        ? tableClasses.cellRootClass ||
                            getRootCellClassname(headerIndex)
                        : getRootCellClassname(headerIndex)
                    )}
                    showCellFullData={showCellFullData}
                    handleCellValueChanged={handleCellValueChanged}
                    handleUpdate={handleBlur}
                  />
                );
              })}
            </TableRow>
          );
        })
      ) : (
        <EmptyTableRow
          width={Object.keys(headers).length}
          height={38 * pageSize}
          classes={classes}
        >
          No Data
        </EmptyTableRow>
      )}
      {emptyRows > 0 && <TableRow style={{ height: 38 * emptyRows }} />}
    </TableBody>
  );
};

export default EnhancedTableBody;
